import { useState } from "react";
import * as Lucide from "lucide-react";
import cn from "classnames";
import Icon, { ICONS } from "@/components/Core/Icons";
import { AI_MODEL, AI_AGENT } from "@/store/zustand/aiActionStore";
import usePlanInfoStore from "@/store/zustand/planInfoStore";
import { MenuItem } from "./ModelMenuItem";
import { Tooltip } from "react-tooltip";
import { MODEL_CONFIG_MAP, CLAUDE_AGENT_MODELS } from "./modelConfig";

interface ClaudeAgentSectionProps {
  agent: AI_AGENT;
  codeGenerationModel: AI_MODEL;
  setAgent: (agent: AI_AGENT) => void;
  setIsOpen: (isOpen: boolean) => void;
  isClaudeCodeDisabled: boolean;
}

export const ClaudeAgentSection = ({
  agent,
  codeGenerationModel,
  setAgent,
  setIsOpen,
  isClaudeCodeDisabled,
}: ClaudeAgentSectionProps) => {
  const [claudeAgentExpanded, setClaudeAgentExpanded] = useState(false);
  const [showUpgradeTooltip, setShowUpgradeTooltip] = useState(false);
  const setShowUpgradeModal = usePlanInfoStore(
    (state) => state.setShowUpgradeModal,
  );

  const claudeAgentModels = CLAUDE_AGENT_MODELS.map((model) => ({
    value: model,
    label: MODEL_CONFIG_MAP[model]?.label || "",
    tooltip: MODEL_CONFIG_MAP[model]?.tooltip,
  }));

  return (
    <div className="relative mb-1">
      <div
        role="button"
        aria-label={
          isClaudeCodeDisabled
            ? "Claude Agent - Upgrade required"
            : claudeAgentExpanded
              ? "Collapse Claude Agent options"
              : "Expand Claude Agent options"
        }
        tabIndex={0}
        className={cn(
          "py-2 px-3 text-xs flex items-center justify-between cursor-pointer rounded-md transition-colors",
          "hover:bg-secondary-n-900",
          agent === AI_AGENT.CLAUDE_AGENT && "bg-secondary-n-950",
          isClaudeCodeDisabled &&
            "opacity-50 cursor-not-allowed hover:bg-transparent",
        )}
        onClick={(e) => {
          if (isClaudeCodeDisabled) {
            setShowUpgradeModal(true);
          } else {
            e.stopPropagation();
            setClaudeAgentExpanded(!claudeAgentExpanded);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            if (isClaudeCodeDisabled) {
              setShowUpgradeModal(true);
            } else {
              setClaudeAgentExpanded(!claudeAgentExpanded);
            }
          }
        }}
        onMouseEnter={() => {
          if (isClaudeCodeDisabled) {
            setShowUpgradeTooltip(true);
          }
        }}
        onMouseLeave={() => {
          setShowUpgradeTooltip(false);
        }}
        data-tooltip-id={
          !isClaudeCodeDisabled ? "claude-agent-tooltip" : undefined
        }
      >
        <div className="flex items-center">
          <Icon
            icon={ICONS.CLAUDE}
            className="h-4 w-4 text-secondary-900 mr-1"
          />
          <span className="font-medium">Claude Agent</span>
        </div>
        {!isClaudeCodeDisabled && (
          <Lucide.ChevronRight className="h-3.5 w-3.5 text-gray-400" />
        )}
      </div>

      {showUpgradeTooltip && isClaudeCodeDisabled && (
        <div
          className="absolute left-full top-0 w-64 bg-popover border border-border rounded-lg p-3 text-xs text-white shadow-lg z-100 ml-3"
          onMouseEnter={() => setShowUpgradeTooltip(true)}
          onMouseLeave={() => setShowUpgradeTooltip(false)}
        >
          <div className="font-semibold mb-2">Upgrade Required</div>
          <div className="text-gray-300 mb-3">
            Claude code is only supported as an add-on for customers with a paid
            Tempo plan
          </div>
          <button
            onClick={() => {
              setShowUpgradeModal(true);
              setShowUpgradeTooltip(false);
            }}
            className="text-primary-n-600 hover:text-primary-n-400 underline inline-flex items-center bg-transparent border-none cursor-pointer p-0"
          >
            Upgrade Now
          </button>
        </div>
      )}

      {/* Claude Agent Models - Side Panel */}
      {claudeAgentExpanded && !isClaudeCodeDisabled && (
        <div className="absolute left-full top-0 ml-3 w-48 bg-popover border border-border rounded-lg p-2 shadow-lg z-100">
          {claudeAgentModels.map(({ value, label, tooltip }) => (
            <MenuItem
              key={value}
              value={value}
              agent={AI_AGENT.CLAUDE_AGENT}
              label={<span>{label}</span>}
              isSelected={
                agent === AI_AGENT.CLAUDE_AGENT && codeGenerationModel === value
              }
              onClose={() => setIsOpen(false)}
              tooltip={tooltip}
            />
          ))}
        </div>
      )}

      {/* Claude Agent Info Tooltip */}
      {!isClaudeCodeDisabled && (
        <Tooltip
          id="claude-agent-tooltip"
          className="bg-black! text-white text-[0.625rem] leading-relaxed font-medium p-3"
          place="top"
          opacity={1}
        >
          <div className="space-y-3">
            <div>
              <div className="font-semibold mb-1">Claude Agent</div>
              <ul className="opacity-80 ml-5 space-y-0.5 list-disc">
                <li>Anthropic's official coding agent</li>
                <li>Slower but most thorough and accurate</li>
                <li>Best for the hardest problems</li>
                <li>Requires Claude Code auth</li>
              </ul>
            </div>
            <div>
              <div className="font-semibold mb-1">Tempo Agent</div>
              <ul className="opacity-80 ml-5 space-y-0.5 list-disc">
                <li>Similar capabilities</li>
                <li>Faster and more cost-effective</li>
              </ul>
            </div>
          </div>
        </Tooltip>
      )}
    </div>
  );
};
